import { useCallback, useRef } from 'react'

/**
 * Returns a debounced callback that fires immediately on the first call (leading edge),
 * then suppresses all subsequent calls for `delayMs` milliseconds.
 * After the cooldown expires, if any calls were suppressed, it fires once more (trailing edge)
 * and resets — ready for the next cycle.
 *
 * Cycle:
 *   1. First call → execute immediately, start cooldown timer
 *   2. Calls during cooldown → mark as pending, do NOT execute
 *   3. Timer expires → if any pending call, execute once and reset
 *   4. Ready for a new cycle
 */
export const useLeadingTrailingDebounce = (callback: () => void, delayMs: number) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasPendingRef = useRef(false)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const debouncedCallback = useCallback(() => {
    // If no timer is running, this is the leading edge → fire immediately
    if (timerRef.current === null) {
      callbackRef.current()

      // Start cooldown
      timerRef.current = setTimeout(() => {
        timerRef.current = null

        if (hasPendingRef.current) {
          hasPendingRef.current = false
          callbackRef.current()
        }
      }, delayMs)
    } else {
      // Cooldown active → mark that we have a pending trailing call
      hasPendingRef.current = true
    }
  }, [delayMs])

  return debouncedCallback
}
